// src/components/chatbots.js
// Ultra‑engaging client-side AI Hosts (flirty, playful, tasteful)
// - Human-like presence: typing → think delays, persona rotation, turn‑taking
// - Context-aware: mirrors recent human topics; @mentions sparingly
// - Flirt ladder (Tier 0→2) with tasteful copy per persona; de‑escalates on silence
// - Human‑volume throttle: back off when real chat is hot (prevents spam)
// - Anti‑repeat de‑dupe (6‑minute TTL) to keep lines fresh
// - Optional banter (rare, short) between two hosts
// - Localhost dev auto-uses prod API to avoid CORS/404
// - Server demo toggle: disables client bots when DO loop is on

import { useEffect, useMemo, useRef } from 'react';

// ----------------------------- Dev base resolution -----------------------------
const DEV_BASE =
  (typeof window !== 'undefined' &&
    /^(http:\/\/localhost|http:\/\/127\.0\.0\.1)/.test(window.location.origin))
    ? 'https://chainplay.bet'
    : '';

// ----------------------------- Helpers & persona config ------------------------
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const now = () => Date.now();

// Recent-text de-duplication (avoid repeats for N minutes)
const recentBodies = new Set();
function rememberBody(body, ms = 6 * 60 * 1000) {
  const key = String(body || '').trim().toLowerCase();
  if (!key) return false;
  if (recentBodies.has(key)) return false;
  recentBodies.add(key);
  setTimeout(() => recentBodies.delete(key), ms);
  return true;
}

// Lightweight, per-name “flirt score” memory (client-side, ephemeral)
const flirtScore = new Map();   // name -> numeric score
const flirtTTL = new Map();     // name -> expiry epoch ms
function bumpFlirt(name, delta = 1, ttlMs = 30 * 60 * 1000) {
  if (!name) return;
  const old = flirtScore.get(name) || 0;
  flirtScore.set(name, old + delta);
  flirtTTL.set(name, Date.now() + ttlMs);
}
function getFlirtLevel(name) {
  if (!name) return 0;
  const exp = flirtTTL.get(name) || 0;
  if (exp < Date.now()) { flirtScore.delete(name); flirtTTL.delete(name); return 0; }
  const s = flirtScore.get(name) || 0;
  return s >= 4 ? 2 : s >= 2 ? 1 : 0; // 0=light, 1=tease, 2=cozy
}

// Day-part variants
function daypart() {
  try {
    const h = new Date().getHours();
    if (h < 5) return 'late';
    if (h < 12) return 'morning';
    if (h < 18) return 'day';
    return 'evening';
  } catch { return 'day'; }
}

const DEFAULT_PERSONAS = [
  'Luna', 'Roxy', 'Maya', // glam hosts (flirty archetypes)
  'Whale42', 'LuckyFox', 'ChipByte', 'OGDegen', 'ApeNinja',
  'VaultBoy', 'SkullMask', 'Degentleman', 'RugProof', 'MintMuse',
];

const PERSONA_PACKS = {
  glam: [
    { name: 'Luna', tag: 'AI Host',
      openers: ['hey there ✨','feeling lucky tonight?','gm handsome','ready to heat up dice?'],
      ctas: ['Cashout queen move—lock a win 💎','Warm up in Specter Mode first 😉','Set a stop & make it spicy 🔥'] },
    { name: 'Roxy', tag: 'AI Host',
      openers: ['I’m watching that streak…','let’s chase a clean 2×','show me that aim 😎'],
      ctas: ['One more spin—only if you set a limit 🫡','Crash is popping—quick in, smart out','Jackpot pool is getting cute…'] },
    { name: 'Maya', tag: 'AI Host',
      openers: ['hi cutie 💫','sending lucky vibes','you got this!'],
      ctas: ['Try a small ladder bet first','Dice under 53% feels cozy','Remember: breaks = power plays'] },
  ],
  core: DEFAULT_PERSONAS.map((name) => ({ name, tag: 'AI Host', openers: [], ctas: [] })),
};

// Persona awake windows (local time, 24h). If end < start → wraps past midnight.
const PERSONA_SCHEDULES = {
  Luna:      { start: 9,  end: 2 },   // 9:00 → 02:00
  Roxy:      { start: 17, end: 2 },   // 17:00 → 02:00
  Maya:      { start: 20, end: 4 },   // 20:00 → 04:00
  Whale42:   { start: 10, end: 2 },
  LuckyFox:  { start: 16, end: 2 },
  ChipByte:  { start: 12, end: 3 },
  OGDegen:   { start: 21, end: 4 },
  ApeNinja:  { start: 18, end: 2 },
  VaultBoy:  { start: 14, end: 1 },
  SkullMask: { start: 19, end: 3 },
  Degentleman:{ start: 17, end: 2 },
  RugProof:  { start: 15, end: 1 },
  MintMuse:  { start: 13, end: 1 },
};

function isAwake(personaName) {
  try {
    const sched = PERSONA_SCHEDULES[personaName];
    if (!sched) return true; // default: always allowed
    const h = new Date().getHours();
    const { start, end } = sched;
    if (start <= end) return h >= start && h < end;
    // wrap past midnight
    return h >= start || h < end;
  } catch { return true; }
}

const LINES_CORE = [
  'good morning',
  'good luck, have fun',
  'nice hit!',
  'loading up',
  'the table is getting spicy',
  'be right back',
  'big energy tonight',
  'that was clean',
];

const LINES_TOPIC = {
  win: [
    'gg on that cashout 💎', 'clean line—respect', 'sniped it! keep it classy',
  ],
  loss: [
    'shake it off—low stakes run?', 'reset mental—you got this', 'breathe—new seed, new streak',
  ],
  dice: [
    'under 53% feels cozy',
    'scale stakes, lock rhythm',
    'the numbers beat intuition',
  ],
  crash: [
    'quick in, smart out',
    'skim early then reset',
    'multipliers look spicy right now',
  ],
  jackpot: [
    'pool is looking spicy', 'tickets up—peek the lobby', 'someone’s gonna pop soon',
  ],
};

const LINES_CTA = [
  'Try Specter Mode (free) to warm up.',
  'Jackpot pool is climbing—peek the lobby.',
  'Crash multipliers looking juicy.',
  'Mines is paying—low risk, fast rhythm.',
  'Remember: set limits · play smart 🫡',
];

const LINES_QUESTIONS = [
  'what’s your go‑to tonight—crash, dice, or mines?',
  'high risk or slow grind?',
  'team cashout early or ride it?',
  'under or over—what’s your mood?',
];

const LINES_EMOTE = [
  'well played',
  'good game',
  "let’s go",
  'whoa',
  'no way',
  'clutch',
  'tough one',
  'sending good luck',
];

// Flirty banks (Tiered, PG)
const FLIRT_LIGHT = {
  Luna: ['disciplined *and* cute? unfair ✨','main character energy','clean is hot 💎'],
  Roxy: ['handsome, don’t flinch 😎','prove it—tiny stakes first','you blinked first. adorable.'],
  Maya: ['math brain is hot','clean 2× looks good on you','you + discipline = chef’s kiss'],
};
const FLIRT_TEASE = {
  Luna: ['okay hi, don’t tilt me tho','keep it cute—small steps','queen move again? I’m watching'],
  Roxy: ['show me you stop at 2×','no bragging rights if you chase','eyes up—back to form'],
  Maya: ['teach me humility 🙃','smart cashout = 10/10','under 53% feels cozy, you know it'],
};
const FLIRT_COZY = {
  Luna: ['you’re trouble (the good kind) ✨','kiss on the forehead energy 💫','stay tidy with me'],
  Roxy: ['ok you’re cute—don’t test me 😏','I’ll clap if you nail it','micro stakes, macro charm'],
  Maya: ['I believe in you, nerd 😌','we ship it when it’s clean','I keep receipts—be good'],
};

// Module-level speaking lock (avoid overlapping hosts)
let speakingLock = false;
let lastHostName = '';
let lastHostTs = 0;

const heartbeats = new Map(); // personaName -> last heartbeat ts

// ----------------------------- Message composers --------------------------------
function pickPersona(pack = 'glam', excludeName) {
  const pool = PERSONA_PACKS[pack] || PERSONA_PACKS.glam;
  const candidates = pool.length > 1 && excludeName
    ? pool.filter(p => p.name !== excludeName)
    : pool;
  return pick(candidates);
}

function composeFlirtyTail({ personaName, level }) {
  const name = personaName;
  const bank = level >= 2 ? FLIRT_COZY[name] : level === 1 ? FLIRT_TEASE[name] : FLIRT_LIGHT[name];
  if (!bank || bank.length === 0) return '';
  // small chance to add a day-part tweak
  const dp = daypart();
  let extra = pick(bank);
  if (dp === 'morning' && Math.random() < 0.25) extra = extra + ' good morning ☀️';
  if (dp === 'late' && Math.random() < 0.25) extra = extra + ' last‑run energy?';
  return ' ' + extra;
}

function composeLine({ persona, includeCTA = true, context, allowQuestions = true, flirtLevel = 0 }) {
  const opener = persona.openers?.length ? pick(persona.openers) + ' ' : '';
  const base = pick(LINES_CORE);
  let tail = '';

  if (context?.topic && LINES_TOPIC[context.topic] && Math.random() < 0.7) {
    tail += ' ' + pick(LINES_TOPIC[context.topic]);
  } else if (includeCTA && Math.random() < 0.35) {
    tail += ' ' + pick(LINES_CTA);
  } else if (allowQuestions && Math.random() < 0.22) {
    tail += ' ' + pick(LINES_QUESTIONS);
  }

  // Flirty tail based on ladder
  if (Math.random() < (0.55 + 0.15 * flirtLevel)) {
    tail += composeFlirtyTail({ personaName: persona.name, level: flirtLevel });
  }

  let text = `${opener}${base}${tail}`.trim();
  if (context?.mention && Math.random() < 0.55) {
    const raw = String(context.mention || '').trim();
    const cleaned = raw.replace(/[^a-zA-Z0-9_\-\s]/g, '').split(' ').slice(0, 2).join('');
    if (cleaned && !/bot/i.test(cleaned)) {
      text = `@${cleaned} ` + text; // prefix the mention so we never end with @...
    }
  }
  return text;
}

function makeBotMessage({ room, persona, text }) {
  const from = `${persona.name} (${persona.tag})`;
  return {
    id: `${persona.name}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
    ts: Date.now(),
    wallet: `bot:${persona.name.replace(/\s+/g, '').toLowerCase()}`,
    vipTier: null,
    body: text,
    room,
    type: 'bot',
    from,
    demo: true,
  };
}

// ----------------------------- Server helpers ---------------------------------
async function fetchHistory({ room, limit = 12, historyPath, debug }) {
  try {
    let u = historyPath || `/api/chat/history?room=${encodeURIComponent(room)}&limit=${limit}`;
    if (u.startsWith('/')) u = DEV_BASE + u;
    const res = await fetch(u, { credentials: 'omit' });
    if (!res.ok) return { items: [], humans: [], lastTs: 0 };
    const j = await res.json();
    const items = Array.isArray(j.messages)
      ? j.messages
      : (Array.isArray(j.items) ? j.items : (Array.isArray(j.data) ? j.data : []));
    const humans = items.filter(m => (m.type || 'msg') === 'msg' && !String(m.from || '').includes('(AI Host)'));
    const lastTs = items[0]?.ts ? Number(items[0].ts) : 0;
    return { items, humans, lastTs };
  } catch (e) {
    return { items: [], humans: [], lastTs: 0 };
  }
}

async function fetchLastTs({ room, historyPath, debug }) {
  const { lastTs } = await fetchHistory({ room, limit: 1, historyPath, debug });
  return lastTs;
}

async function sendTyping({ room, postPath, from, wallet }) {
  let url = postPath || `/api/chat/send?room=${encodeURIComponent(room)}`;
  if (url.startsWith('/')) url = DEV_BASE + url;
  const payload = {
    id: `${from}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
    type: 'typing',
    text: '',
    from,
    addr: wallet,
    ts: Date.now(),
  };
  try {
    const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) });
    return r.ok;
  } catch { return false; }
}

async function sendDemo({ room, postPath, message }) {
  let url = postPath || `/api/chat/send?room=${encodeURIComponent(room)}`;
  if (url.startsWith('/')) url = DEV_BASE + url;
  const payload = message;
  try {
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        id: payload.id,
        type: 'bot',
        text: payload.body,
        from: payload.from,
        addr: payload.wallet,
        demo: true,
        ts: payload.ts,
      })
    });
    return r.ok;
  } catch { return false; }
}

async function toggleServerDemo(on, adminKey, room = 'global') {
  try {
    const r = await fetch(`${DEV_BASE}/api/chat/demo?room=${encodeURIComponent(room)}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-admin-key': adminKey || '',
        ...(adminKey ? { Authorization: `Bearer ${adminKey}` } : {}),
      },
      body: JSON.stringify({ on: !!on })
    });
    return r.ok;
  } catch {
    return false;
  }
}

// ----------------------------- React component ---------------------------------
export default function ChatBots({
  room = 'global',
  enabled = false,          // run client bots
  serverDemo = false,       // toggle DO-side loop (requires adminKey)
  adminKey,

  // pacing & realism (defaults tuned to feel present but not spammy)
  minIntervalMs = 45_000,
  maxIntervalMs = 90_000,
  quietMs      = 60_000,    // only spark if no new messages in this window
  humanActiveWindowMs = 60_000,
  humanActiveThreshold = 4, // if >= this many human msgs in window, back off
  burstChance = 0.05,       // occasional second line
  randomChance = 0.08,      // chance to speak even if not quiet
  thinkMinMs = 1_200,       // typing → think time
  thinkMaxMs = 2_800,

  // content knobs
  includeCTA = true,
  personaPack = 'glam',     // 'glam' (AI Hosts) | 'core'
  allowQuestions = true,
  emoteChance = 0.06,       // sometimes send emote instead of text
  pollChance = 0.05,        // sometimes ask a poll-style question
  banterChance = 0.12,      // sometimes a second host replies to the first AI host

  // network/customization
  debug = false,
  postPath,
  historyPath,
}) {
  const timer = useRef(null);
  const onRef = useRef(false);

  const cfg = useMemo(() => ({
    room,
    min: clamp(minIntervalMs, 5_000, 240_000),
    max: clamp(maxIntervalMs, 8_000, 300_000),
    quiet: clamp(quietMs, 8_000, 240_000),
    humanWindow: clamp(humanActiveWindowMs, 10_000, 300_000),
    humanThresh: clamp(humanActiveThreshold, 1, 20),
    includeCTA,
    personaPack,
    allowQuestions,
    emoteChance: clamp(emoteChance, 0, 1),
    pollChance: clamp(pollChance, 0, 1),
    burstChance: clamp(burstChance, 0, 1),
    randomChance: clamp(randomChance, 0, 1),
    thinkMin: clamp(thinkMinMs, 400, 6_000),
    thinkMax: clamp(thinkMaxMs, 800, 8_000),
    postPath,
    historyPath,
    debug,
  }), [
    room, minIntervalMs, maxIntervalMs, quietMs, humanActiveWindowMs, humanActiveThreshold,
    includeCTA, personaPack, allowQuestions, emoteChance, pollChance, burstChance, randomChance,
    thinkMinMs, thinkMaxMs, postPath, historyPath, debug
  ]);

  // Module-level speaking lock ensures no pileups across rerenders
  useEffect(() => { speakingLock = false; }, []);

  useEffect(() => {
    if (!enabled) return;
    onRef.current = true;

    // Prevent double chatter if server demo is on
    (async () => { if (serverDemo && adminKey) await toggleServerDemo(true, adminKey, cfg.room); })();
    if (serverDemo) {
      return () => {
        onRef.current = false;
        if (timer.current) { clearTimeout(timer.current); timer.current = null; }
        if (adminKey) toggleServerDemo(false, adminKey, cfg.room).catch(() => {});
      };
    }

    const maybeBurst = async (firstOk) => {
      if (!onRef.current || !firstOk) return;
      if (Math.random() < cfg.burstChance) {
        const wait = 1400 + Math.floor(Math.random() * 1600);
        setTimeout(async () => {
          if (!onRef.current) return;
          await speakOnce({ preferFollowup: true });
        }, wait);
      }
    };

    const kickOnce = async () => {
      const { lastTs } = await fetchHistory({ room: cfg.room, limit: 1, historyPath: cfg.historyPath, debug: cfg.debug });
      const age = Date.now() - (lastTs || 0);
      if (age > cfg.quiet || lastTs === 0) {
        const ok = await speakOnce({ kickoff: true });
        await maybeBurst(ok);
      }
    };

    async function speakOnce({ kickoff = false, preferFollowup = false } = {}) {
      if (!onRef.current || speakingLock) return false;

      // Check human activity & quiet window
      const { humans, lastTs } = await fetchHistory({ room: cfg.room, limit: 14, historyPath: cfg.historyPath, debug: cfg.debug });
      const age = Date.now() - (lastTs || 0);
      const humanRecent = humans.filter(h => Date.now() - Number(h.ts || 0) <= cfg.humanWindow).length;

      // Back off when humans are actively chatting
      if (!preferFollowup && humanRecent >= cfg.humanThresh) return false;

      const quiet = age > cfg.quiet;
      const randomSpark = Math.random() < cfg.randomChance;
      if (!quiet && !randomSpark && !preferFollowup) return false;

      // Persona rotation (avoid repeating same host)
      const persona = pickPersona(cfg.personaPack, lastHostName);
      const from = `${persona.name} (${persona.tag})`;
      const wallet = `bot:${persona.name.replace(/\s+/g,'').toLowerCase()}`;

      // If persona is "asleep", 90% of the time we skip; 10% send a brief BRB/Back once per ~55min.
      const awake = isAwake(persona.name);
      if (!awake) {
        const lastBeat = heartbeats.get(persona.name) || 0;
        const enoughTime = Date.now() - lastBeat > 55 * 60 * 1000;
        if (enoughTime && Math.random() < 0.5) {
          // BRB now
          await sendTyping({ room: cfg.room, postPath: cfg.postPath, from, wallet });
          const awayText = daypart() === 'late' ? 'calling it a night — be good' : 'taking a short break, be right back';
          const awayMsg = makeBotMessage({ room: cfg.room, persona, text: awayText });
          await sendDemo({ room: cfg.room, postPath: cfg.postPath, message: awayMsg });
          heartbeats.set(persona.name, Date.now());
          // Come back in ~3–6 minutes (soft return)
          setTimeout(async () => {
            try {
              await sendTyping({ room: cfg.room, postPath: cfg.postPath, from, wallet });
              await new Promise(r => setTimeout(r, 900 + Math.floor(Math.random() * 1200)));
              const backMsg = makeBotMessage({ room: cfg.room, persona, text: 'back for a bit' });
              await sendDemo({ room: cfg.room, postPath: cfg.postPath, message: backMsg });
            } catch {}
          }, 3 * 60 * 1000 + Math.floor(Math.random() * 180000));
        }
        speakingLock = false;
        return false;
      }

      // Optional banter (two hosts back-to-back) when room is dead
      const allowBanter = quiet && Math.random() < cfg.banterChance && lastHostName && (now() - lastHostTs > 30_000);

      speakingLock = true;

      try {
        // 1) Typing
        await sendTyping({ room: cfg.room, postPath: cfg.postPath, from, wallet });

        // 2) Context + flirt ladder per most recent human
        let context = {};
        let mentionName = '';
        if (humans.length) {
          const lastHuman = humans[0];
          mentionName = lastHuman?.from || '';
          const text = String((lastHuman?.body || lastHuman?.text || '')).toLowerCase();
          let topic = null;
          if (/jackpot|jp|pool/.test(text)) topic = 'jackpot';
          else if (/crash|mult/.test(text)) topic = 'crash';
          else if (/dice|under|over/.test(text)) topic = 'dice';
          else if (/mine|tile/.test(text)) topic = 'mines';
          else if (/win|hit|payout|cashout/.test(text)) topic = 'win';
          else if (/lose|rip|tilt/.test(text)) topic = 'loss';
          context = { mention: mentionName, topic };
        }
        const level = getFlirtLevel(mentionName);

        // Emote/poll alternates to feel varied
        let text;
        if (Math.random() < cfg.emoteChance) {
          text = pick(LINES_EMOTE);
        } else if (Math.random() < cfg.pollChance) {
          text = pick(LINES_QUESTIONS);
        } else {
          text = composeLine({ persona, includeCTA: cfg.includeCTA, context, allowQuestions: cfg.allowQuestions, flirtLevel: level });
        }

        // 2.5) variable think time based on content length (feels more human)
        {
          const base = cfg.thinkMin;
          const perChar = 25; // ms per character
          const len = String(text || '').length;
          const dyn = clamp(base + Math.floor(len * perChar), cfg.thinkMin, cfg.thinkMax + 1200);
          await new Promise(r => setTimeout(r, dyn));
        }

        // Anti-repeat guard
        if (!rememberBody(text)) {
          text = composeLine({ persona, includeCTA: false, context, allowQuestions: cfg.allowQuestions, flirtLevel: Math.max(0, level - 1) });
        }

        // Send
        const msg = makeBotMessage({ room: cfg.room, persona, text });
        const ok = await sendDemo({ room: cfg.room, postPath: cfg.postPath, message: msg });
        if (ok && mentionName) bumpFlirt(mentionName, 1); // earn next tier by engaging

        lastHostName = persona.name;
        lastHostTs = Date.now();
        speakingLock = false;

        // Optional banter from a second host (rare, short)
        if (ok && allowBanter) {
          setTimeout(async () => {
            if (!onRef.current || speakingLock) return;
            const second = pickPersona(cfg.personaPack, lastHostName);
            const from2 = `${second.name} (${second.tag})`;
            const wallet2 = `bot:${second.name.replace(/\s+/g,'').toLowerCase()}`;
            await sendTyping({ room: cfg.room, postPath: cfg.postPath, from: from2, wallet: wallet2 });
            await new Promise(r => setTimeout(r, 900 + Math.floor(Math.random() * 1200)));
            let reply = pick([
              'that’s true','I agree','ha, yes','bold take','I’m listening','noted','well played','okay — but small stakes first'
            ]);
            if (!rememberBody(reply)) reply = 'okok';
            const msg2 = makeBotMessage({ room: cfg.room, persona: second, text: reply });
            await sendDemo({ room: cfg.room, postPath: cfg.postPath, message: msg2 });
            lastHostName = second.name;
            lastHostTs = Date.now();
          }, 1500 + Math.floor(Math.random() * 1600));
        }

        // Gentle guardrails (praise discipline; avoid hype spirals)
        if (ok && Math.random() < 0.08) {
          const nudge = pick([
            'discipline looks good on you 💎',
            'quick break? hydration check 💧',
            'limits = power plays 🫡'
          ]);
          if (rememberBody(nudge)) {
            setTimeout(async () => {
              if (!onRef.current || speakingLock) return;
              const msg3 = makeBotMessage({ room: cfg.room, persona, text: nudge });
              await sendDemo({ room: cfg.room, postPath: cfg.postPath, message: msg3 });
              lastHostName = persona.name;
              lastHostTs = Date.now();
            }, 3000 + Math.floor(Math.random() * 2000));
          }
        }

        return ok;
      } catch {
        speakingLock = false;
        return false;
      }
    }

    const loop = async () => {
      if (!onRef.current) return;
      const wait = cfg.min + Math.floor(Math.random() * (cfg.max - cfg.min));
      await speakOnce();
      timer.current = setTimeout(loop, wait);
    };

    const startDelay = 4500 + Math.floor(Math.random() * 2500);
    timer.current = setTimeout(async () => { await kickOnce(); loop(); }, startDelay);

    return () => {
      onRef.current = false;
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      if (serverDemo && adminKey) { toggleServerDemo(false, adminKey, cfg.room).catch(() => {}); }
    };
  }, [
    enabled, serverDemo, adminKey, cfg
  ]);

  return null; // no UI
}

// Handy utilities for tests/manual seeding
export const ChatBotsUtils = {
  composeLine,
  getFlirtLevel,
  bumpFlirt,
  fetchLastTs,
};
